import { useRef } from 'react';
import { App, Button, Descriptions, Modal, Space, Tag, Typography } from 'antd';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import dayjs from 'dayjs';
import type { EnrollmentTokenDto } from '@/types/enrollment';

const { Text, Paragraph } = Typography;

interface Props {
  token: EnrollmentTokenDto | null;
  onClose: () => void;
}

export function ViewQrModal({ token, onClose }: Props) {
  const { message } = App.useApp();
  const qrContainerRef = useRef<HTMLDivElement>(null);

  if (!token) return null;

  const handleCopyToken = () => {
    navigator.clipboard.writeText(token.tokenValue);
    message.success('Token copiado al portapapeles.');
  };

  const handleCopyUrl = () => {
    if (token.qrCodeUrl) {
      navigator.clipboard.writeText(token.qrCodeUrl);
      message.success('URL copiada al portapapeles.');
    }
  };

  /**
   * Convierte el SVG renderizado del QR a PNG y lo descarga.
   * Como QRCodeSVG renderiza un <svg>, lo serializamos, lo metemos en una <img>,
   * lo dibujamos en un canvas y exportamos a PNG.
   */
  const handleDownloadPng = () => {
    if (!qrContainerRef.current) return;
    const svg = qrContainerRef.current.querySelector('svg');
    if (!svg) return;

    const svgString = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const size = 800; // PNG de alta resolucion para impresion
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Fondo blanco (importante para impresion)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);

      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `enrollment-qr-${token.id}-${token.label?.replace(/[^a-z0-9]/gi, '_') || 'token'}.png`;
      link.href = pngUrl;
      link.click();

      URL.revokeObjectURL(url);
      message.success('QR descargado.');
    };
    img.src = url;
  };

  return (
    <Modal
      title={`QR de enrolamiento ${token.label ? `- ${token.label}` : `#${token.id}`}`}
      open={!!token}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>Cerrar</Button>,
        <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadPng}>
          Descargar PNG
        </Button>,
      ]}
      width={600}
      destroyOnClose
    >
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div
          ref={qrContainerRef}
          style={{
            display: 'inline-block',
            padding: 24,
            backgroundColor: '#fff',
            borderRadius: 8,
            border: '1px solid var(--color-border, #d9d9d9)',
          }}
        >
          <QRCodeSVG
            value={token.qrCodeJson}
            size={320}
            level="M"
            includeMargin={false}
          />
        </div>

        <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0, fontSize: 13 }}>
          Escanealo desde un dispositivo Android nuevo (o reseteado a fabrica) durante el
          setup wizard. Para activar el lector de QR, tap 6 veces en la pantalla de bienvenida.
        </Paragraph>
      </div>

      <Descriptions
        bordered
        size="small"
        column={1}
        labelStyle={{ width: 160, fontWeight: 500 }}
      >
        <Descriptions.Item label="Enterprise">{token.enterpriseName}</Descriptions.Item>
        <Descriptions.Item label="Perfil">
          {token.profileName ? <Tag color="blue">{token.profileName}</Tag> : <Text type="secondary">—</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Estado">
          {token.isUsable
            ? <Tag color="success">Activo</Tag>
            : token.isRevoked ? <Tag color="error">Revocado</Tag>
            : token.isExpired ? <Tag color="default">Expirado</Tag>
            : <Tag color="warning">Usado</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Tipo de uso">
          {token.oneTimeOnly ? <Tag color="orange">Un solo uso</Tag> : <Tag color="blue">Multiples usos</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Veces usado">{token.usedCount}</Descriptions.Item>
        <Descriptions.Item label="Expira">
          {dayjs(token.expiresAt).format('DD/MM/YYYY HH:mm')}
        </Descriptions.Item>
        {token.qrCodeUrl && (
          <Descriptions.Item label="URL alternativa">
            <Space>
              <Text style={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {token.qrCodeUrl}
              </Text>
              <Button size="small" icon={<CopyOutlined />} onClick={handleCopyUrl}>
                Copiar
              </Button>
            </Space>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Token raw">
          <Space>
            <Text style={{ fontSize: 11, fontFamily: 'monospace' }}>
              {token.tokenValue.slice(0, 32)}...
            </Text>
            <Button size="small" icon={<CopyOutlined />} onClick={handleCopyToken}>
              Copiar completo
            </Button>
          </Space>
        </Descriptions.Item>
      </Descriptions>
    </Modal>
  );
}
